/**
 * Container for an Audio component.
 */
import React from 'react'
import { connect } from 'react-redux'
import { Dispatch, AnyAction } from 'redux'

import { RootStoreState } from '../../root/index'
import { Collection, Screen } from '../../api/types'
import * as actions from '../../screen/actions'
import * as audioActions from '../actions'
import { PlayerStatus, PreviewData, AudioSectionContainer, PlaylistItem, AudioPreview } from '../types'
// parsePlaylistId/getPlaylistId not needed here — looking up by the stored
// currentPlaylistId string avoids a round-trip that mangles undefined fileIds
// (URL-sourced audio has no file.id, so the key contains "undefined") into NaN.

export interface OwnProps {
	collectionId?: number
	container?: AudioSectionContainer
	audioPreview?: AudioPreview
	fullScreenEnabled?: boolean
	coverImages?: any[]
	startTimes?: any[]
	height?: number | string
	isFullscreenSlider?: boolean
	screen?: Screen
	slideshowEnabled?: boolean
}

export interface Props extends OwnProps {
	collection?: Collection
	currentAudio?: PlaylistItem
}

export interface Actions {
	loadAudioPreview: (container: AudioSectionContainer, audio: AudioPreview, data: PreviewData) => void
	clearAudioPath: (fileName: string) => void
	setCurrentTime: (audioId: number, time: number, container: AudioSectionContainer, fileId: number) => void
	audioLoaded: (audioId: number, duration: number, container: AudioSectionContainer, fileId: number) => void
	loadAudioError: (audioId: number, error: unknown, container: AudioSectionContainer, fileId: number) => void
	setPlayerStatus: (audioId: number, status: PlayerStatus, container: AudioSectionContainer, fileId: number) => void
	setAudioEnded: (audioId: number, container: AudioSectionContainer, fileId: number) => void
}

const mapStateToProps = (state: RootStoreState, ownProps: OwnProps): Props => {
	const currentAudio: PlaylistItem | undefined = state.audioplayer.playlist.get(state.audioplayer.currentPlaylistId)

	const collection = state.collectionlist.collectionDetails.get(
		ownProps.collectionId || (currentAudio && currentAudio.container?.collectionId) || -1,
	)

	const item: Collection | undefined = collection && collection.details ? collection.details : undefined

	return {
		collectionId: ownProps.collectionId,
		collection: item,
		audioPreview: ownProps.audioPreview,
		height: ownProps.height,
		isFullscreenSlider: ownProps.isFullscreenSlider,
		currentAudio,
		screen: ownProps.screen,
	}
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): Actions => ({
	loadAudioPreview: (container: AudioSectionContainer, audio: AudioPreview, data: PreviewData) => {
		dispatch(audioActions.loadAudioPreview({ container, audio, data }))
	},
	setCurrentTime: (audioId: number, time: number, container: AudioSectionContainer, fileId: number) => {
		dispatch(audioActions.setCurrentTime({ audioId, time, container, fileId }))
	},
	audioLoaded: (audioId: number, duration: number, container: AudioSectionContainer, fileId: number) => {
		dispatch(audioActions.audioLoaded({ audioId, duration, container, fileId }))
	},
	setPlayerStatus: (audioId: number, status: PlayerStatus, container: AudioSectionContainer, fileId: number) => {
		dispatch(audioActions.setPlayerStatus({ audioId, status, container, fileId }))
	},
	setAudioEnded: (audioId: number, container: AudioSectionContainer, fileId: number) => {
		dispatch(audioActions.setAudioEnded({ audioId, container, fileId }))
	},
	loadAudioError: (audioId: number, error: unknown, container: AudioSectionContainer, fileId: number) => {
		dispatch(audioActions.loadAudioError({ audioId, error, container, fileId }))
	},
	clearAudioPath: (fileName: string) => {
		dispatch(actions.clearAudioPath(fileName))
	},
})

export default (Component: React.ComponentType<Props & Actions>) =>
	connect(mapStateToProps, mapDispatchToProps)(Component)
